import { AnimatePresence, motion } from "motion/react";
import { useMenu } from "../../contexts/menu/menu.hook";
import { MenuContentOptions, MenuProps } from "../../contexts/menu/Menu.types";
import { useVideoSettings } from "../../contexts/videoSettings/videoSettings.hook";
import { MenuContentNavigation } from "./menuContent/navigation.component";
import { MenuContentTrailer } from "./menuContent/trailer.component";
import { MenuContentSynopsis } from "./menuContent/synopsis.component";
import { usePrefersReducedMotion } from "../../contexts/prefersReducedMotion/prefersReducedMotion.hook";
import styles from "./Menu.module.scss";
import { MenuContentCastCrew } from "./menuContent/castcrew.component";

const waveAnimation = {
  d: [
    "M 0,400 C 0,400 0,200 0,200 C 106.57142857142858,238.75 213.14285714285717,277.5 348,251 C 482.85714285714283,224.5 646,132.75000000000003 758,135 C 870,137.24999999999997 930.8571428571429,233.5 1036,260 C 1141.142857142857,286.5 1290.5714285714284,243.25 1440,200 C 1440,200 1440,400 1440,400 Z",
    "M 0,400 C 0,400 0,200 0,200 C 120,186.71428571428572 240,173.42857142857142 358,194 C 476,214.57142857142858 591.9999999999999,269 706,252 C 820.0000000000001,234.99999999999997 932,146.57142857142856 1054,126 C 1176,105.42857142857143 1308,152.71428571428572 1440,200 C 1440,200 1440,400 1440,400 Z",
    "M 0,400 C 0,400 0,200 0,200 C 119,241.5 238,283 375,263 C 512,243 666.9999999999999,161.5 781,153 C 895.0000000000001,144.5 968,209 1071,229 C 1174,249 1307,224.5 1440,200 C 1440,200 1440,400 1440,400 Z",
    "M 0,400 C 0,400 0,200 0,200 C 141.10714285714286,205.57142857142856 282.2142857142857,211.14285714285714 392,206 C 501.7857142857143,200.85714285714286 580.25,185 677,173 C 773.75,161 888.7857142857142,152.85714285714286 1019,158 C 1149.2142857142858,163.14285714285714 1294.607142857143,181.57142857142856 1440,200 C 1440,200 1440,400 1440,400 Z",
    "M 0,400 C 0,400 0,200 0,200 C 106.57142857142858,238.75 213.14285714285717,277.5 348,251 C 482.85714285714283,224.5 646,132.75000000000003 758,135 C 870,137.24999999999997 930.8571428571429,233.5 1036,260 C 1141.142857142857,286.5 1290.5714285714284,243.25 1440,200 C 1440,200 1440,400 1440,400 Z",
  ],
  transition: {
    repeat: Infinity,
    duration: 3,
    ease: "linear",
  },
};

export default function Menu({
  synopsisData,
  buyTicketsLink,
  trailerLink,
}: MenuProps) {
  const { isPlayingIntroVideo } = useVideoSettings();
  const { isMenuOpen, menuBg, menuContent } = useMenu();
  const { prefersReducedMotion } = usePrefersReducedMotion();

  const menuVariants = {
    hidden: { y: prefersReducedMotion ? 0 : "-150vh" },
    visible: { y: 0 },
  };

  const renderMenuContent = () => {
    switch (menuContent) {
      case MenuContentOptions.NAVIGATION:
        return <MenuContentNavigation buyTicketsLink={buyTicketsLink} />;
      case MenuContentOptions.TRAILER:
        return <MenuContentTrailer trailerLink={trailerLink} />;
      case MenuContentOptions.SYNOPSIS:
        return <MenuContentSynopsis synopsisData={synopsisData} />;
      case MenuContentOptions.CAST_CREW:
        return <MenuContentCastCrew />;
      default:
        break;
    }
  };

  return (
    <AnimatePresence>
      {isMenuOpen && !isPlayingIntroVideo && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="hidden"
          transition={{ type: "tween", duration: 1 }}
          variants={menuVariants}
          className={styles.menuWrapper}
          style={{
            backgroundColor: menuBg,
          }}
        >
          {renderMenuContent()}

          {!prefersReducedMotion && (
            <div className={styles.menuSvgWrapper}>
              <svg
                width="100%"
                height="100%"
                id="svg"
                viewBox="0 0 1440 400"
                xmlns="http://www.w3.org/2000/svg"
                className="transition duration-300 ease-in-out delay-150"
              >
                <motion.path
                  animate={waveAnimation}
                  stroke="none"
                  strokeWidth="0"
                  fill={menuBg}
                  fillOpacity="1"
                  transform="rotate(-180 720 200)"
                  initial={{
                    d: "M 0,400 C 0,400 0,200 0,200 C 106.57142857142858,238.75 213.14285714285717,277.5 348,251 C 482.85714285714283,224.5 646,132.75000000000003 758,135 C 870,137.24999999999997 930.8571428571429,233.5 1036,260 C 1141.142857142857,286.5 1290.5714285714284,243.25 1440,200 C 1440,200 1440,400 1440,400 Z",
                  }}
                />
              </svg>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
